import {LinearProgress, withStyles} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import {Skeleton} from '@material-ui/lab';
import {startCase, toLower} from 'lodash';
import React from 'react';
import {FormattedNumber} from 'react-intl';
import {StudioTooltip} from '../../../base-components/StudioTooltip';
import './ImageCardDetail.scss';

type ImageCardDetailData = {
  label: string;
  progress: number;
};

export type ImageCardDetailProps = {
  data: ImageCardDetailData[];
  onHover?: (stats: ImageCardDetailData, index: number | null) => void;
  countObjects: boolean;
  truncateLabel?: boolean;
};

const BorderLinearProgress = withStyles({
  root: {
    height: 10,
    backgroundColor: 'var(--greyscale2)',
  },
  bar: {
    borderRadius: 0,
    backgroundColor: 'var(--primary3)',
  },
})(LinearProgress);

export function ImageCardDetail({
  data,
  onHover,
  countObjects,
  truncateLabel = false,
}: ImageCardDetailProps) {
  const labelsCount = countObjects
    ? data.reduce(function(rv, x) {
        const number = rv.get(x.label);
        if (!number) {
          rv.set(x.label, 1);
        } else {
          rv.set(x.label, number + 1);
        }
        return rv;
      }, new Map<string, number>())
    : new Map<string, number>();

  return (
    <React.Fragment>
      {data.length ? (
        data.map((value, index) => {
          const labelText = startCase(toLower(value.label ?? ''));
          const label = truncateLabel ? `${labelText?.slice(0, 10)}...` : labelText;
          const title = countObjects
            ? `${labelText} (${labelsCount.get(value.label)})`
            : labelText;
          return (
            <div
              key={index}
              className="image-card-detail"
              onPointerEnter={_event => {
                onHover?.(value, index);
              }}
              onPointerLeave={_event => {
                onHover?.(value, null);
              }}
            >
              <StudioTooltip
                title={title}
                PopperProps={{popperOptions: {positionFixed: true}}}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  className="image-card-detail__title"
                >
                  {label}
                </Typography>
              </StudioTooltip>
              <div className="image-card-detail__progress">
                <BorderLinearProgress
                  variant="determinate"
                  value={value.progress * 100}
                />
              </div>
              <Typography
                className="image-card-detail__stats"
                variant="body2"
                align="right"
                color="textSecondary"
              >
                <FormattedNumber
                  style="percent" // eslint-disable-line react/style-prop-object
                  maximumFractionDigits={1}
                  value={value.progress}
                />
              </Typography>
            </div>
          );
        })
      ) : (
        <Skeleton animation="wave" variant="rect" height={20} />
      )}
    </React.Fragment>
  );
}
